/**
 * Voice Answer Trigger Detector
 *
 * Detects trigger phrases that indicate the user is about to answer a rehearsal question.
 * When detected, subsequent speech is captured as the answer.
 */

// Trigger phrases that indicate an answer is starting
const TRIGGER_PHRASES = [
  'to answer your question',
  'to answer that question',
  'to answer this',
  'to answer that',
  'my answer is',
  'the answer is',
  'i would say',
  'well',
  'so',
];

// Phrases that might be false positives (questions, not answers)
const EXCLUDE_PATTERNS = [
  /^(what|how|why|when|where|who|which|can you|could you)/i,
  /\?$/,
];

export interface TriggerResult {
  triggered: boolean;
  answerText: string;
  triggerPhrase: string | null;
}

/**
 * Detect if the transcript contains an answer trigger phrase
 * and extract any text that follows it as the beginning of the answer.
 *
 * @param transcript - The recent transcript text to analyze
 * @returns Object with triggered flag, answer text, and which phrase triggered it
 */
export function detectAnswerTrigger(transcript: string): TriggerResult {
  const lowerTranscript = transcript.toLowerCase().trim();

  // Skip if it looks like a question
  for (const pattern of EXCLUDE_PATTERNS) {
    if (pattern.test(lowerTranscript)) {
      return { triggered: false, answerText: '', triggerPhrase: null };
    }
  }

  // Check each trigger phrase
  for (const phrase of TRIGGER_PHRASES) {
    const index = lowerTranscript.indexOf(phrase);
    if (index !== -1) {
      // Extract everything after the trigger phrase
      const afterTrigger = transcript.slice(index + phrase.length).trim();

      // Clean up common leading words
      let answerText = afterTrigger
        .replace(/^[,.:;]\s*/, '') // Remove leading punctuation
        .replace(/^(that|is|it's|it is)\s+/i, '') // Remove filler words
        .trim();

      return {
        triggered: true,
        answerText,
        triggerPhrase: phrase,
      };
    }
  }

  return { triggered: false, answerText: '', triggerPhrase: null };
}

/**
 * Check if text indicates the user is still speaking
 * (not just silence or short utterances)
 */
export function isSubstantiveText(text: string): boolean {
  const trimmed = text.trim();
  // Consider text substantive if it has at least 3 words
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  return words.length >= 3;
}

/**
 * Estimate if the user seems to be done speaking based on text patterns.
 * This is a heuristic - actual silence detection is handled separately.
 */
export function seemsComplete(text: string): boolean {
  const trimmed = text.trim();

  // Ends with sentence-ending punctuation
  if (/[.!?]$/.test(trimmed)) {
    return true;
  }

  // Has multiple sentences
  const sentences = trimmed.split(/[.!?]+/).filter(s => s.trim().length > 0);
  if (sentences.length >= 2) {
    return true;
  }

  // Has enough words to be a reasonable answer (20+ words suggests a complete thought)
  const words = trimmed.split(/\s+/).filter(w => w.length > 0);
  if (words.length >= 20) {
    return true;
  }

  return false;
}
